import { AppSimpleSelect, FieldRow } from 'components/FormElements';
import PageSection from 'components/PageSection';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Controller, useForm } from 'react-hook-form';
import { carrySettings } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import {
  CarriedInterestDayCountFormat,
  CarriedInterestFeeAllocationMethod,
  WaterfallStructure,
} from 'further-types/lp/carry-settings';
import DraggableList from 'components/DraggableList';
import WaterfallStructureItem from './WaterfallStructureItem';
import { Alert, styled, Typography } from '@mui/material';
import useOverallCarriedInterestSettings, {
  FormValues,
} from './hooks/useOverallCarriedInterestSettings';
import { waterfallStructureLabels } from 'further-ui/labels';
import FixedWaterfallStructureItem from './FixedWaterfallStructureItem';
import BasicSelect from 'further-ui/components/BasicSelect';
import useDayCountOptions from 'hooks/lp/data/vehicle/useDayCountOptions';

const StyledDraggableList = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    carrySettings: {
      dayCount: CarriedInterestDayCountFormat;
      feeAllocationMethod: CarriedInterestFeeAllocationMethod;
      waterfallStructure: Array<WaterfallStructure>;
    };
  };
};

const OverallCarriedInterestSettings: React.FC<Props> = ({ vehicle }) => {
  const {
    feeAllocationMethodOptions,
    defaultWaterfallStructure,
    updateSettings,
    isPending,
  } = useOverallCarriedInterestSettings();
  const dayCountOptions = useDayCountOptions();

  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(carrySettings.OverallCarriedInterestSettings),
    shouldFocusError: true,
    values: {
      ...vehicle.carrySettings,
      waterfallStructure:
        vehicle.carrySettings.waterfallStructure.length > 1
          ? vehicle.carrySettings.waterfallStructure
          : defaultWaterfallStructure,
    },
    criteriaMode: 'all',
  });

  const onSubmit = (data: FormValues) => {
    updateSettings({
      vehicleId: vehicle._id,
      ...data,
    });
  };

  const draggableWaterfallItems: Array<WaterfallStructure> = watch(
    'waterfallStructure',
  ).filter((item) => item !== WaterfallStructure.ExcessProceeds);

  return (
    <PageSection
      headerContent={
        <Typography variant="h4">Overall carried interest settings</Typography>
      }
      withSpacing
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Spacing>
          <FieldRow
            title="Day count"
            centerTitle
            tooltipText="This is the day count option used for calculating the preferred return across the fund vehicle."
          >
            <Controller
              name="dayCount"
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, value },
              }) => (
                <BasicSelect
                  size="small"
                  fullWidth
                  options={dayCountOptions}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                />
              )}
            />
          </FieldRow>
          <FieldRow
            title="Fee allocation method"
            centerTitle
            tooltipText={
              <>
                <i>Investor-specific</i> calculates fees based on each investor
                individually, reflecting each LP's specific terms and capital
                commitments. <i>Commitment pro-rata</i> calculates a fund level
                fee based on total commitment and splits it between investors
                (i.e. 10% of the total fund commitment equals 10% of the fee).
              </>
            }
          >
            <Controller
              name="feeAllocationMethod"
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, value },
              }) => (
                <AppSimpleSelect
                  options={feeAllocationMethodOptions}
                  fullWidth
                  error={!!error}
                  value={value}
                  onChange={(e) =>
                    onChange(
                      e.target.value as CarriedInterestFeeAllocationMethod,
                    )
                  }
                />
              )}
            />
          </FieldRow>
          <FieldRow
            title="Waterfall structure"
            tooltipText="Select the steps in the vehicle’s waterfall for use in distribution calculations."
          >
            <Controller
              name="waterfallStructure"
              control={control}
              render={({ fieldState: { error }, field: { onChange } }) => (
                <Spacing direction="column" size="sm">
                  <StyledDraggableList
                    onReorder={({ orderedItemIds: newOrder }) => {
                      onChange([
                        ...newOrder,
                        WaterfallStructure.ExcessProceeds,
                      ]);
                    }}
                  >
                    {draggableWaterfallItems.map((item, index) => (
                      <DraggableList.Item
                        id={item.toString()}
                        key={item.toString()}
                      >
                        <WaterfallStructureItem
                          label={waterfallStructureLabels[item]}
                          index={index}
                        />
                      </DraggableList.Item>
                    ))}
                  </StyledDraggableList>
                  <FixedWaterfallStructureItem
                    label={
                      waterfallStructureLabels[
                        WaterfallStructure.ExcessProceeds
                      ]
                    }
                    index={draggableWaterfallItems.length}
                  />
                  {error && <Alert severity="error">{error.message}</Alert>}
                </Spacing>
              )}
            />
          </FieldRow>
          <ButtonGroup>
            <Button
              size="large"
              loading={isPending}
              variant="contained"
              type="submit"
            >
              Save changes
            </Button>
          </ButtonGroup>
        </Spacing>
      </form>
    </PageSection>
  );
};

export default OverallCarriedInterestSettings;
